/**
 * AttentionSuccessDialog (KAN-761, KAN-903)
 *
 * AttentionSuccessFlow: what "Успех" of a client opens (logic: useAttentionSuccess):
 * - the order compose of the client's cart with "Пропустить" under it;
 * - without orders (closed to the user by api_access, or the organization is
 *   not an e-commerce one) the plain dialog "Успех: <клиент>" with "Отметить
 *   успех" (AttentionSuccessDialog).
 * Closing either dialog sends nothing.
 */
import { ThumbsUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { OrderComposeDialog } from '@/components/orders/OrderComposeDialog'
import type { AttentionItem } from '@/services/api/dashboard'
import { attentionItemTitle } from './attentionFormat'
import type { AttentionSuccess } from './useAttentionSuccess'

interface AttentionSuccessDialogProps {
  /** Client being closed; null: the dialog is closed. */
  item: AttentionItem | null
  onCancel: () => void
  onMarkOnly: () => void
}

/** Plain "Успех: <клиент>" without orders. */
export function AttentionSuccessDialog({ item, onCancel, onMarkOnly }: AttentionSuccessDialogProps) {
  return (
    <Dialog
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
    >
      <DialogContent className="max-w-md" data-testid="attention-success-dialog">
        <DialogHeader>
          <DialogTitle>Успех: {item ? attentionItemTitle(item) : ''}</DialogTitle>
          <DialogDescription>Отметить успех по клиенту.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" onClick={onMarkOnly}>
            <ThumbsUp className="mr-1 h-3.5 w-3.5" />
            Отметить успех
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Footer of the compose opened by "Успех": the client and "Пропустить". */
function SuccessComposeFooter({ item, onSkip }: { item: AttentionItem | null; onSkip: () => void }) {
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-sm text-gray-600"
      data-testid="attention-success-compose-footer"
    >
      <span>Успех: {item ? attentionItemTitle(item) : ''}. Оформите заказ или пропустите: будет отмечен только успех.</span>
      <Button type="button" variant="outline" size="sm" onClick={onSkip}>
        <ThumbsUp className="mr-1 h-3.5 w-3.5" />
        Пропустить
      </Button>
    </div>
  )
}

/** Everything "Успех" opens: the order compose or the plain dialog. */
export function AttentionSuccessFlow({ success }: { success: AttentionSuccess }) {
  return (
    <>
      <AttentionSuccessDialog item={success.dialogItem} onCancel={success.cancel} onMarkOnly={success.markOnly} />
      <OrderComposeDialog
        orderId={success.composeOrderId}
        onClose={success.closeCompose}
        onPlaced={success.placed}
        footer={<SuccessComposeFooter item={success.composeItem} onSkip={success.skipOrder} />}
      />
    </>
  )
}
